import type React from 'react';
import * as Headless from '@headlessui/react';
import clsx from 'clsx';

/**
 * A grouping of related form controls.
 */
export function Fieldset({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.FieldsetProps, 'as' | 'className'>) {
  return (
    <Headless.Fieldset
      {...props}
      className={clsx(
        className,
        '*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6'
      )}
    />
  );
}

/**
 * The caption for a fieldset.
 */
export function Legend({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.LegendProps, 'as' | 'className'>) {
  return (
    <Headless.Legend
      data-slot="legend"
      {...props}
      className={clsx(
        className,
        'text-base/6 font-semibold text-gray-950 data-disabled:opacity-50 sm:text-sm/6 dark:text-white'
      )}
    />
  );
}

/**
 * Groups multiple form fields together with consistent spacing.
 */
export function FieldGroup({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      data-slot="control"
      {...props}
      className={clsx(className, 'space-y-8')}
    />
  );
}

/**
 * A single form field container for label, control, description, and error message.
 */
export function Field({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.FieldProps, 'as' | 'className'>) {
  return (
    <Headless.Field
      {...props}
      className={clsx(
        className,
        '[&>[data-slot=label]+[data-slot=control]]:mt-3',
        '[&>[data-slot=label]+[data-slot=description]]:mt-1',
        '[&>[data-slot=description]+[data-slot=control]]:mt-3',
        '[&>[data-slot=control]+[data-slot=description]]:mt-3',
        '[&>[data-slot=control]+[data-slot=error]]:mt-3',
        '*:data-[slot=label]:font-medium'
      )}
    />
  );
}

/**
 * The label for a form field.
 */
export function Label({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<Headless.LabelProps, 'as' | 'className'>) {
  return (
    <Headless.Label
      data-slot="label"
      {...props}
      className={clsx(
        className,
        'text-base/6 text-gray-950 select-none data-disabled:opacity-50 sm:text-sm/6 dark:text-white'
      )}
    />
  );
}

/**
 * A brief description or hint for a form field.
 */
export function Description({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DescriptionProps,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'text-base/6 text-gray-500 data-disabled:opacity-50 sm:text-sm/6 dark:text-gray-400'
      )}
    />
  );
}

/**
 * An error message for a form field.
 */
export function ErrorMessage({
  className,
  ...props
}: { 
  /** Optional CSS class name. */
  className?: string 
} & Omit<
  Headless.DescriptionProps,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      data-slot="error"
      {...props}
      className={clsx(
        className,
        'text-base/6 text-red-600 data-disabled:opacity-50 sm:text-sm/6 dark:text-red-500'
      )}
    />
  );
}
